<script setup>
    // Vue
    import { ref, defineEmits, inject } from 'vue';
    import { defineExpose } from 'vue';

    // Component
    import PasswordInput from '@/components/PasswordInputComponent.vue';
    import Tooltip from '@/components/TooltipComponent.vue';

    // Store
    //import { useCommonStore } from '@/stores/common/commonStore';
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useUserLoginPasswordUpdateStore } from '@/stores/modal/userLoginPasswordUpdateStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utils
    import Utils from '../../commonProject/helpers/utils.js';

    // Else
    import i18n from '@/common/i18n';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    // ----------------------------
    // パスワード変更
    // ----------------------------
    const userLoginPasswordStore = useUserLoginPasswordUpdateStore();
    const loginStore = useLoginStore();
    const passwordForm = ref({ password: '', newPassword: '' });
    //const commonStore = useCommonStore();
    const checkPassword = ref('');
    const API_URL = ref('');

    /**
     * 入力情報リセット
     */
    function resetPasswordFrom() {
        passwordForm.value.password = '';
        passwordForm.value.newPassword = '';
        checkPassword.value = '';

        // 本モーダルを表示した際に、パスワード入力欄HTML要素の初期化処理を実行する。
        passwordInputComponent1.value.resetInput();
        passwordInputComponent2.value.resetInput();
        passwordInputComponent3.value.resetInput();
    }

    // ----------------------------
    // 親画面で実装必須関数
    // ----------------------------
    const emit = defineEmits(['userLoginPasswordUpdateModal:cancel', 'userLoginPasswordUpdateModal:update']);

    async function openModal(url) {
        // 初期化する。
        resetPasswordFrom();

        // 実行するAPIのURLを継承する。
        API_URL.value = url;
    }

    function cancel() {
        resetPasswordFrom();
        // 親画面のモーダル非表示処理を呼び出す
        emit('userLoginPasswordUpdateModal:cancel');
    }

    async function update() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const response = await userLoginPasswordStore.updateNewCognitoPasswordAfterLoginAction(passwordForm.value.password, passwordForm.value.newPassword);
        switch (response.status) {
            case 'change-password-success':
                {
                    // ユーザーパスワード更新完了APIをコールする。クエリパラメーターの条件は設定しない。
                    const response1 = await loginStore.postUserLoginPasswordUpdateedDateUpdateAction(API_URL.value, null);

                    if (response1.success) {
                        resetPasswordFrom();
                        // 親画面の更新後処理を呼び出す
                        emit('userLoginPasswordUpdateModal:update');
                    } else {
                        messageDialog('error', response1.errorMessage);
                        if (response1.redirect) {
                            location.href = response1.redirectUrl;
                        }
                        return;
                    }
                }

                break;
            case 'change-password-failed':
                // 通常のパスワードエラー。
                messageDialog('error', i18n.global.t('common.message.cognitoError'));
                break;
            case 'no-password-reuse':
                // パスワードの使いまわしエラー。
                messageDialog('error', i18n.global.t('common.message.noPasswordReuse'));
                break;
            case 'NO SESSION':
                messageDialog('error', i18n.global.t('common.message.errorSession'));
                break;
            default:
                // 何もしない。
                break;
        }
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 現在のパスワードの入力チェック。
        if (Utils.isNullEmpty(passwordForm.value.password)) {
            message += i18n.global.t('userLoginPasswordUpdate.message.message1') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(passwordForm.value.password)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message2') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberSymbol(passwordForm.value.password)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message3') + '\n';
            }
        }

        // 新しいパスワードの入力チェック。
        if (Utils.isNullEmpty(passwordForm.value.newPassword)) {
            message += i18n.global.t('userLoginPasswordUpdate.message.message4') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(passwordForm.value.newPassword)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message5') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberSymbol(passwordForm.value.newPassword)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message6') + '\n';
            }
        }

        // 新しいパスワード(確認)の入力チェック。
        if (Utils.isNullEmpty(checkPassword.value)) {
            message += i18n.global.t('userLoginPasswordUpdate.message.message7') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(checkPassword.value)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message8') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberSymbol(checkPassword.value)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message9') + '\n';
            }
        }

        // パスワード入力の不一致。
        if (!Utils.isStringMatch(passwordForm.value.newPassword, checkPassword.value)) {
            message += i18n.global.t('common.message.emptyPasswordUnmatch3') + '\n';
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    defineExpose({
        openModal,
    });

    // パスワードHTML要素コンポーネントを宣言する。
    // 複数存在する場合は、ユニークなコンポーネント名を設定する。
    const passwordInputComponent1 = ref(null);
    const passwordInputComponent2 = ref(null);
    const passwordInputComponent3 = ref(null);
</script>

<template>
    <article id="modalChangePassword" class="modal" :style="{ display: 'block' }">
        <h3>{{ $t('userLoginPasswordUpdate.parts.modalTitle1') }}</h3>
        <section class="modalContents double">
            <section class="form">
                <dl>
                    <!--現在のパスワード-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('userLoginPasswordUpdate.parts.dl1') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <PasswordInput ref="passwordInputComponent1" v-model:input="passwordForm.password" :isPropsPasswordInputDisabled="false" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--新しいパスワード-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('userLoginPasswordUpdate.parts.dl2') }}</label>
                            </li>
                            <li>
                                <Tooltip :classBottom="true">{{ $t('userLoginPasswordUpdate.message.blowoutInformation1') }}</Tooltip>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <PasswordInput ref="passwordInputComponent2" v-model:input="passwordForm.newPassword" :isPropsPasswordInputDisabled="false" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--新しいパスワード(確認)-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('userLoginPasswordUpdate.parts.dl3') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <PasswordInput ref="passwordInputComponent3" v-model:input="checkPassword" :isPropsPasswordInputDisabled="false" />
                            </li>
                        </ul>
                    </dd>
                </dl>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <!--キャンセル-->
                    <button type="button" class="btnNormal" @click="cancel">{{ $t('common.parts.cancel') }}</button>
                </li>
                <li>
                    <!--登録-->
                    <button type="button" class="btnPrimary" @click="update" :disabled="disabledStore.isDisabled">{{ $t('common.parts.entry') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>
<style scoped>
    /* この画面だけのスタイルを上書きする */
    #overlay .modal .modalContents > .form > dl > dt {
        width: 224px;
    }
    #overlay .modal .modalContents > .form > dl > dd {
        width: calc(100% - 224px);
    }
    @media screen and (max-width: 768px) {
        #overlay .modal .modalContents > .form > dl > dt {
            width: 100%;
            margin-bottom: 2px;
        }
        #overlay .modal .modalContents > .form > dl > dd {
            width: 100%;
        }
    }
    #overlay .modal .modalContents > .form > dl > dt > ul.formLabel > li {
        width: auto;
    }
    #overlay .modal .modalContents > .innerTable table.table thead tr th:nth-child(1),
    #overlay .modal .modalContents > .innerTable table.table tbody tr td:nth-child(1) {
        width: 150px;
        padding-left: 10px;
    }
    #overlay .modal .modalContents > .innerTable table.table thead tr th:nth-child(2),
    #overlay .modal .modalContents > .innerTable table.table tbody tr td:nth-child(2) {
        width: 150px;
    }
</style>
